import React, {
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { FieldLabel } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import {
  v4 as uuidv4,
  validate as uuidValidate,
} from 'uuid';
import {
  Button,
  Input,
} from 'reactstrap';

import ColorPickerField from '../Common/ColorPickerField';
import FontPickerField from '../Common/FontPickerField';
import {
  DEFAULT_GOOGLE_WEIGHTS,
  normalizeFontPickerValue,
} from '../Common/pageFontUtils';
import { showModalTop } from '../../../../actions';

const getUploadedDisplayName = (font = {}) => {
  const rawName = String(
    font.fileName
    || font.original_name
    || font.display_name
    || font.path?.split('/').pop()
    || '',
  ).trim();

  return rawName.replace(/\.[^.]+$/, '');
};

const isUuidLikeName = (value = '') => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return false;
  }

  if (uuidValidate(normalizedValue)) {
    return true;
  }

  const baseName = normalizedValue.replace(/\.[^.]+$/, '');
  return uuidValidate(baseName);
};

const getDeterministicLocalKey = (font = {}, index = 0) => {
  const source = font.source || 'uploaded';
  const identity = font.uuid || font.path || font.googleFamily || `index-${index}`;
  return `puck-font-${source}-${identity}`;
};

const FontItem = ({
  item,
  index,
  canReorder,
  onChange,
  onRemove,
  connectDragSource,
  connectDropTarget,
  onShowModalTop,
}) => {
  const intl = useIntl();
  const nameInputRef = useRef(null);
  const nameSelectionRef = useRef(null);

  const handlePickerChange = (updated) => {
    const currentName = (item.name || '').trim();
    const nextName = (updated?.name || '').trim();
    const googleSelectionCleared = updated?.source === 'google' && !updated?.googleFamily;
    const googleFamilyChanged = updated?.source === 'google'
      && !!updated?.googleFamily
      && updated.googleFamily !== item.googleFamily;

    if (googleSelectionCleared) {
      onChange(index, { ...updated, name: '' });
      return;
    }

    if (googleFamilyChanged) {
      onChange(index, { ...updated, name: updated.googleFamily });
      return;
    }

    if (updated?.source === 'uploaded' && updated?.uploadedViaLogoUploader) {
      const uploadedName = getUploadedDisplayName(updated);
      const currentUploadedName = getUploadedDisplayName(item);
      const shouldApplyUploadedName = !!uploadedName && (
        !currentName
        || currentName === currentUploadedName
        || isUuidLikeName(currentName)
      );

      if (shouldApplyUploadedName) {
        onChange(index, { ...updated, name: uploadedName });
        return;
      }
    }

    if (!currentName && !nextName && updated?.source === 'google') {
      const googleName = updated.googleFamily || 'Inter';
      onChange(index, { ...updated, name: googleName });
      return;
    }

    onChange(index, updated);
  };

  useLayoutEffect(() => {
    if (!nameSelectionRef.current) {
      return;
    }

    const input = nameInputRef.current;
    if (!input || document.activeElement !== input) {
      return;
    }

    const { start, end } = nameSelectionRef.current;
    input.setSelectionRange(start, end);
    nameSelectionRef.current = null;
  }, [item.name]);

  const dragHandle = canReorder ? connectDragSource((
    <span
      className="puck-fonts-field-drag-handle"
      title={intl.formatMessage({ defaultMessage: 'Drag to reorder' })}
    >
      <i className="fas fa-grip-vertical" />
    </span>
  )) : null;

  return connectDropTarget((
    <div className="puck-fonts-field-item">
      <FontPickerField
        value={item}
        onChange={handlePickerChange}
        leadingControl={dragHandle}
        trailingControl={(
          <Button size="sm" color="link" className="puck-fonts-field-remove" onClick={() => onRemove(index)}>
            <i className="far fa-trash" />
          </Button>
        )}
        onOpenFilePicker={(handleFileData) => {
          onShowModalTop('FileSelectModalEnhanced', {
            className: 'modal-select-file modal-select-file-enhanced',
            handleSelectFile: () => {},
            handleFileUrl: () => {},
            handleFileData,
            allowUpload: true,
            allowedFileExtensions: ['ttf', 'otf', 'woff', 'woff2'],
            showThumbnail: false,
          });
        }}
      />
      <ColorPickerField
        value={item.color || '#222222'}
        onChange={color => onChange(index, { ...item, color })}
        label={`${intl.formatMessage({ defaultMessage: 'Color' })}:`}
        className="puck-fonts-field-color-row"
        buttonClassName="puck-fonts-field-color-btn"
        inputClassName="puck-fonts-field-color-input"
        popoverClassName="puck-fonts-field-color-picker-popover"
        defaultColor="#222222"
      />
      <Input
        innerRef={nameInputRef}
        bsSize="sm"
        className="mb-1"
        placeholder={intl.formatMessage({ defaultMessage: 'Font name' })}
        value={item.name || ''}
        onChange={(e) => {
          nameSelectionRef.current = {
            start: e.target.selectionStart,
            end: e.target.selectionEnd,
          };
          onChange(index, { ...item, name: e.target.value });
        }}
      />
      <Input
        bsSize="sm"
        className="mb-1"
        placeholder={intl.formatMessage({ defaultMessage: 'Description' })}
        value={item.description || ''}
        onChange={e => onChange(index, { ...item, description: e.target.value })}
      />
    </div>
  ));
};

FontItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  canReorder: PropTypes.bool.isRequired,
  moveFont: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

const puckFontSource = {
  beginDrag(props) {
    return {
      id: props.item.localKey,
      index: props.index,
    };
  },

  canDrag(props) {
    return props.canReorder;
  },
};

const puckFontTarget = {
  hover(props, monitor) {
    const dragItem = monitor.getItem();
    const dragIndex = dragItem.index;
    const hoverIndex = props.index;

    if (dragItem.id === props.item.localKey || dragIndex === hoverIndex) {
      return;
    }

    props.moveFont(dragIndex, hoverIndex);

    /* eslint-disable no-param-reassign */
    dragItem.index = hoverIndex;
    /* eslint-enable */
  },
};

const DraggableFontItem =
DragSource('puckFontItem', puckFontSource, DNDconnect => ({
  connectDragSource: DNDconnect.dragSource(),
}))(DropTarget('puckFontItem', puckFontTarget, DNDconnect => ({
  connectDropTarget: DNDconnect.dropTarget(),
}))(FontItem));

const mapDispatchToProps = (dispatch) => ({
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedDraggableFontItem = connect(null, mapDispatchToProps)(DraggableFontItem);

const FontsField = ({ value, onChange }) => {
  const intl = useIntl();
  const rawFonts = Array.isArray(value) ? value : [];
  const fonts = rawFonts.map((font, index) => {
    const base = font && typeof font === 'object' && !Array.isArray(font) ? font : {};
    const normalizedPickerValue = normalizeFontPickerValue(font);

    return {
      ...base,
      ...normalizedPickerValue,
      localKey: base.localKey || getDeterministicLocalKey(base, index),
      name: base.name || '',
      description: base.description || '',
      color: base.color || '#222222',
    };
  });

  useEffect(() => {
    const shouldNormalize = rawFonts.length !== fonts.length
      || rawFonts.some(font => !font || typeof font !== 'object' || Array.isArray(font) || !font.localKey);

    if (shouldNormalize) {
      onChange(fonts);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (index, updated) => {
    const next = fonts.map((f, i) => (i === index ? updated : f));
    onChange(next);
  };

  const moveFont = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const reorderedFonts = [...fonts];
    const [movedItem] = reorderedFonts.splice(fromIndex, 1);
    reorderedFonts.splice(toIndex, 0, movedItem);
    onChange(reorderedFonts);
  };

  const handleRemove = (index) => {
    onChange(fonts.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([
      ...fonts,
      {
        source: 'google',
        localKey: uuidv4(),
        uuid: '',
        path: '',
        fileName: '',
        uploadedViaLogoUploader: false,
        name: '',
        description: intl.formatMessage({ defaultMessage: 'Example usage' }),
        googleFamily: '',
        googleWeight: '400',
        googleWeights: DEFAULT_GOOGLE_WEIGHTS,
        googleFiles: {},
        googleSearch: '',
      },
    ]);
  };

  const canReorder = fonts.length > 1;

  return (
    <div className="puck-fonts-field-label">
      <FieldLabel label={intl.formatMessage({ defaultMessage: 'Fonts' })}>
        {fonts.map((item, index) => (
          <ConnectedDraggableFontItem
            key={item.localKey || item.uuid || item.googleFamily || `font-${index}`}
            item={item}
            index={index}
            canReorder={canReorder}
            moveFont={moveFont}
            onChange={handleChange}
            onRemove={handleRemove}
          />
        ))}
        <Button size="sm" color="primary" onClick={handleAdd} className="w-100">
          <i className="far fa-plus" /> {intl.formatMessage({ defaultMessage: 'Add font' })}
        </Button>
      </FieldLabel>
    </div>
  );
};

FontsField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

FontsField.defaultProps = {
  value: [],
};

export default FontsField;
